import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Check, Minus } from "lucide-react";

const DATA = {
    turbines: {
        label: "Turbines",
        rows: [
            { spec: "Vitesse maximale", cols: ["≤ 380 000 tr/min", "≤ 420 000 tr/min", "≤ 200 000 tr/min"] },
            { spec: "Couple", cols: ["16 W", "20 W", "22 W"] },
            { spec: "Tête", cols: ["Mini", "Standard", "45° chirurgicale"] },
            { spec: "Spray", cols: ["1 jet", "3 jets", "4 jets"] },
            { spec: "Système Push button", cols: [true, true, true] },
            { spec: "Compatibilité KaVo / NSK", cols: [true, true, false] },
            { spec: "Autoclavable 135 °C", cols: [true, true, true] },
        ],
        models: ["Mini Compact", "KaVo-Compat", "Turbine 45°"],
    },
    contras: {
        label: "Contre-angles",
        rows: [
            { spec: "Rapport", cols: ["1:1", "1:5 (multiplicateur)", "16:1 (réducteur)"] },
            { spec: "Vitesse max", cols: ["40 000 tr/min", "200 000 tr/min", "2 500 tr/min"] },
            { spec: "Usage principal", cols: ["Polissage", "Coupe haute vitesse", "Endodontie / Implanto"] },
            { spec: "Irrigation", cols: ["Externe", "Externe / interne", "Interne"] },
            { spec: "Push button", cols: [true, true, true] },
            { spec: "LED intégrée", cols: [false, true, true] },
            { spec: "Stérilisable", cols: [true, true, true] },
        ],
        models: ["Standard 1:1", "Multiplicateur 1:5", "Réducteur 16:1"],
    },
    endo: {
        label: "Endomoteurs",
        rows: [
            { spec: "Écran", cols: ["LCD 1.8″", "OLED 2.4″", "OLED 3.2″"] },
            { spec: "Mode Reciproc", cols: [false, true, true] },
            { spec: "Apex locator intégré", cols: [false, true, true] },
            { spec: "Plage de couple", cols: ["0.6 – 4 N·cm", "0.6 – 5 N·cm", "0.4 – 6 N·cm"] },
            { spec: "Mémoire programmes", cols: ["8", "16", "24"] },
            { spec: "Bluetooth / mise à jour", cols: [false, false, true] },
            { spec: "Batterie", cols: ["3 h", "5 h", "8 h"] },
        ],
        models: ["Essentiel", "Précision", "Atelier"],
    },
};

const Cell = ({ value }) => {
    if (value === true)
        return (
            <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0a84ff]/10">
                <Check className="w-3.5 h-3.5 text-[#0a84ff]" strokeWidth={2.4} />
            </div>
        );
    if (value === false)
        return (
            <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-neutral-100">
                <Minus className="w-3.5 h-3.5 text-neutral-400" />
            </div>
        );
    return (
        <span className="font-mono-tech text-[13px] text-neutral-700">
            {value}
        </span>
    );
};

const InteractiveComparison = () => {
    const [active, setActive] = useState("turbines");
    const set = DATA[active];

    return (
        <section id="comparatif" className="relative py-24 md:py-32 bg-white">
            <div className="max-w-5xl mx-auto px-6 md:px-10">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="dp-eyebrow">Comparatif interactif</div>
                    <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight font-light text-neutral-900 leading-[1.05]">
                        Comparer en toute clarté.
                    </h2>
                    <p className="mt-4 text-[16px] text-neutral-500">
                        Sélectionnez une famille d'équipement pour confronter modèles, performances
                        et spécifications côte à côte.
                    </p>
                </div>

                <Tabs value={active} onValueChange={setActive} className="mt-12">
                    <TabsList className="mx-auto h-auto p-1.5 rounded-full bg-[#F5F5F7] border border-black/[0.05] grid grid-cols-3 max-w-md">
                        {Object.entries(DATA).map(([k, v]) => (
                            <TabsTrigger
                                key={k}
                                value={k}
                                className="rounded-full py-2.5 text-[13px] data-[state=active]:bg-[#1C1C1E] data-[state=active]:text-white"
                            >
                                {v.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    <TabsContent value={active} className="mt-10">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.4 }}
                                className="rounded-3xl border border-black/[0.06] overflow-hidden bg-white"
                            >
                                <div className="grid grid-cols-4 bg-[#FBFBFD] border-b border-black/[0.05]">
                                    <div className="p-5 text-[12px] font-mono-tech tracking-[0.2em] uppercase text-neutral-500">
                                        Spécification
                                    </div>
                                    {set.models.map((m, i) => (
                                        <div
                                            key={m}
                                            className={`p-5 text-center ${
                                                i === 1 ? "bg-white border-x border-black/[0.05]" : ""
                                            }`}
                                        >
                                            <div className="dp-eyebrow !text-[10px]">Modèle {i + 1}</div>
                                            <div className="mt-1 font-display text-[15px] tracking-tight font-medium text-neutral-900">
                                                {m}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {set.rows.map((r, idx) => (
                                    <div
                                        key={r.spec}
                                        className={`grid grid-cols-4 ${
                                            idx !== set.rows.length - 1
                                                ? "border-b border-black/[0.04]"
                                                : ""
                                        }`}
                                    >
                                        <div className="p-5 text-[14px] text-neutral-700 font-medium">
                                            {r.spec}
                                        </div>
                                        {r.cols.map((v, i) => (
                                            <div
                                                key={i}
                                                className={`p-5 flex items-center justify-center text-center ${
                                                    i === 1 ? "bg-[#FBFBFD]" : ""
                                                }`}
                                            >
                                                <Cell value={v} />
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </motion.div>
                        </AnimatePresence>
                    </TabsContent>
                </Tabs>
            </div>
        </section>
    );
};

export default InteractiveComparison;
